import * as R from 'ramda';
import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// utilities
import { sendRequest } from '../../../utilities/http';
import endpointsMap from '../../../utilities/endpoints';
// components charge
import { mapConfiguredAccessorials } from './helpers';
import { getAccessorialsForState, getAccessorialOptionValue } from '../helpers';
//////////////////////////////////////////////////

const mapRateChargeAccessorials = (charges, accessorials) => R.or(charges, []).map((charge, index) => {
  const { sharedAssessorialGuid } = charge;

  const isShared = G.isNotNilAndNotEmpty(sharedAssessorialGuid);
  const configGuid = charge.assessorialConfigGuid || charge.originalConfigGuid || charge.assessorialGuid;
  const accessorialConfig = accessorials.find(R.propEq(configGuid, GC.FIELD_ORIGINAL_CONFIG_GUID)) || {};

  return {
    ...accessorialConfig,
    ...charge,
    originalConfigGuid: configGuid,
    accessorialSource: isShared ? 'shared' : 'contract',
    sharedAssessorialGuid: isShared ? sharedAssessorialGuid : null,
    displayedValue: charge.displayedValue || charge.name || accessorialConfig.displayedValue,
    guid: (isShared && sharedAssessorialGuid) || charge.guid || charge.id || `rate-charge-${index}`,
  };
});

export const useContractChargeAccessorials = ({
  endpoint,
  loadType,
  accessorials,
  listEndpoint,
  initialCharges,
  initialContractGuid,
}) => {
  const [rateCharges, setRateCharges] = useState([]);
  const [contractAccessorials, setContractAccessorials] = useState([]);

  const requestId = useRef(0);

  useEffect(() => () => { requestId.current += 1; }, []);

  const getContractAccessorials = useCallback(async (contractGuid, requestEndpoint, params = {}) => {
    requestId.current += 1;

    const currentRequestId = requestId.current;

    setContractAccessorials([]);

    if (G.isNilOrEmpty(contractGuid)) return;

    try {
      const response = await sendRequest('get', requestEndpoint, { params: { contractGuid, ...params } });

      if (currentRequestId !== requestId.current) return;

      if (G.isResponseSuccess(response.status)) {
        setContractAccessorials(R.or(response.data, []));
      } else {
        G.handleFailResponseSimple(response);
      }
    } catch (error) {
      if (currentRequestId === requestId.current) G.handleException(error, 'getContractAccessorials');
    }
  }, []);

  const handleGetContractAccessorials = useCallback((contractGuid, charges = []) => {
    setRateCharges(charges);

    return getContractAccessorials(contractGuid, endpoint, { autoApply: false });
  }, [endpoint, getContractAccessorials]);

  useEffect(() => {
    setRateCharges(initialCharges || []);

    getContractAccessorials(initialContractGuid, listEndpoint);
  }, [listEndpoint, initialCharges, initialContractGuid, getContractAccessorials]);

  const options = useMemo(() => {
    const baseAccessorials = R.values(R.or(accessorials, {}));
    const generalAccessorials = baseAccessorials.filter((item) => !item.accessorialSource);
    const mappedContract = mapConfiguredAccessorials(generalAccessorials, contractAccessorials, 'contract');
    const mappedRateCharges = mapRateChargeAccessorials(rateCharges, generalAccessorials);

    const combinedAccessorials = R.uniqBy(getAccessorialOptionValue, [
      ...mappedRateCharges,
      ...mappedContract,
      ...baseAccessorials,
    ]);

    return getAccessorialsForState(combinedAccessorials, loadType, { grouped: true });
  }, [loadType, rateCharges, accessorials, contractAccessorials]);

  return { ...options, handleGetContractAccessorials };
};

export const withContractChargeAccessorials = ({ isCarrier, isCustomer, isCreateMode } = {}) => (Component) => {
  if (!isCustomer && !isCarrier) return Component;

  const endpoint = isCustomer
    ? endpointsMap.customerContractAvailableAssessorials
    : endpointsMap.carrierContractAvailableAssessorials;

  const listEndpoint = isCustomer
    ? endpointsMap.customerContractAssessorialList
    : endpointsMap.carrierContractAssessorialList;

  const contractField = isCustomer ? GC.FIELD_CUSTOMER_CONTRACT_GUID : GC.FIELD_CARRIER_CONTRACT_GUID;

  const ContractChargeAccessorials = (props) => {
    const invoiceData = R.path(['asyncInitialData', 'data'], props) || props.selectedRate || props.initialValues || {};
    const invoiceCharges = invoiceData.charges || (isCarrier ? invoiceData.carrierRateCharges : undefined);
    const initialData = props.isInvoice ? invoiceData : props.initialValues;
    const initialContractGuid = props.isInvoice || isCreateMode === false ? R.prop(contractField, initialData) : null;

    const options = useContractChargeAccessorials({
      endpoint,
      listEndpoint,
      initialContractGuid,
      accessorials: props.accessorials,
      initialCharges: props.isInvoice ? invoiceCharges : undefined,
      loadType: props.loadType || (isCustomer ? GC.LOAD_TYPE_CLO : GC.LOAD_TYPE_TEL),
    });

    return <Component {...props} {...options} />;
  };

  return ContractChargeAccessorials;
};
